import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { generateRecurrencyLink, validateCouponApi } from 'services/api.service';
import { Plan } from 'services/types/recurrency-link';
import { maskCurrency } from 'utils/currency';
import { validateDocument } from 'utils/document';
import { onlyNumbers } from 'utils/number';
import { onlyAlnum } from '@divipay/br-documents';
import { z } from 'zod';

const purchaseSchema = z
  .object({
    name: z.string().min(1, 'Nome é obrigatório'),
    email: z.string().email('Email inválido'),
    phone: z
      .string()
      .min(11, 'Telefone inválido')
      .refine((value) => {
        return onlyNumbers(value).length === 11;
      }, 'Telefone inválido'),
    document: z
      .string()
      .min(11, 'CPF/CNPJ inválido')
      .refine((value) => {
        return validateDocument(value);
      }, 'CPF/CNPJ inválido'),
  })
  .required();

type PurchaseFormData = {
  name: string;
  email: string;
  document: string;
  phone: string;
  reffererId?: string;
  coupon?: string;
};

interface PurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  planId: string;
  reffererId?: string;
  plan: Plan;
}

export function PurchaseModal({ isOpen, onClose, planId, reffererId, plan }: PurchaseModalProps) {
  const toast = useToast();
  const [coupon, setCoupon] = useState('');
  const [validatedCoupon, setValidatedCoupon] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<PurchaseFormData>({
    resolver: zodResolver(purchaseSchema),
  });

  const {
    mutate: validateCoupon,
    isLoading: isLoadingCoupon,
    data: couponData,
  } = useMutation({
    mutationFn: (coupon: string) => validateCouponApi(planId, coupon),
    onSuccess: (data) => {
      setValidatedCoupon(data.coupon);
    },
    onError: (err) => {
      toast({
        title: 'Ops!',
        description:
          (err as AxiosError<{ message: string }>).response?.data?.message || 'Não foi possível validar o cupom.',
        status: 'error',
      });
    },
  });

  const { mutate, isLoading } = useMutation({
    mutationFn: (data: PurchaseFormData) => generateRecurrencyLink(planId, { ...data, coupon: validatedCoupon }),
    onSuccess: (data) => {
      toast({
        title: 'Sucesso!',
        description: 'Seu link de pagamento foi gerado.',
        status: 'success',
      });
      reset();
      onClose();
      // You might want to redirect to the payment URL here
      window.location.href = data.link;
    },
    onError: () => {
      toast({
        title: 'Erro',
        description: 'Não foi possível gerar o link de pagamento.',
        status: 'error',
      });
    },
  });

  const onSubmit = (data: PurchaseFormData) => {
    mutate({ ...data, phone: onlyNumbers(data.phone), document: onlyAlnum(data.document), reffererId });
  };

  useEffect(() => {
    if (isOpen) {
      setValidatedCoupon('');
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Informações para compra</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <VStack spacing={4}>
              <FormControl isInvalid={!!errors.name}>
                <FormLabel>Nome completo</FormLabel>
                <Input
                  {...register('name')}
                  autoComplete="name"
                  placeholder="Nome completo"
                />
                <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.email}>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  autoComplete="email"
                  {...register('email')}
                  placeholder="exemplo@email.com"
                />
                <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.document}>
                <FormLabel>CPF</FormLabel>
                <InputMask
                  mask={'999.999.999-99'}
                  {...register('document')}
                  type="tel"
                  maskChar=" "
                >
                  {(inputProps) => (
                    <Input
                      {...inputProps}
                      type="tel"
                      fontSize={'sm'}
                      ms={{ base: '0px', md: '0px' }}
                      placeholder="000.000.000-00"
                      required
                    />
                  )}
                </InputMask>
                <FormErrorMessage>{errors.document?.message}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.phone}>
                <FormLabel>Telefone</FormLabel>
                <InputMask
                  mask={'(99) 99999-9999'}
                  {...register('phone')}
                  type="tel"
                  maskChar=" "
                >
                  {(inputProps) => (
                    <Input
                      {...inputProps}
                      type="tel"
                      fontSize={'sm'}
                      ms={{ base: '0px', md: '0px' }}
                      placeholder="(00) 00000-0000"
                      required
                    />
                  )}
                </InputMask>
              </FormControl>

              <Text
                fontSize={'lg'}
                color={'gray.500'}
              >
                Valor do plano: <b> {maskCurrency(Number(plan?.amount))}</b>
              </Text>
              <FormControl isInvalid={!!errors.coupon}>
                <FormLabel>Cupom de desconto</FormLabel>
                <Input
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value)}
                  placeholder="Possui cupom de desconto?"
                />
                <FormErrorMessage>{errors.coupon?.message}</FormErrorMessage>
              </FormControl>
              <Button
                colorScheme="blue"
                width="100%"
                isLoading={isLoading || isLoadingCoupon}
                onClick={() => validateCoupon(coupon)}
              >
                Validar cupom
              </Button>

              {validatedCoupon && (
                <>
                  <Text
                    fontSize={'sm'}
                    color={'gray.500'}
                  >
                    Cupom validado:{' '}
                    <b>
                      {validatedCoupon} - {couponData?.discount * 100}%
                    </b>
                  </Text>
                  <Text
                    fontSize={'sm'}
                    color={'gray.500'}
                  >
                    Desconto: {maskCurrency(Number(couponData?.discount) * Number(plan?.amount))}
                  </Text>
                  <Text
                    fontSize={'lg'}
                    color={'gray.600'}
                    fontWeight={'bold'}
                  >
                    Valor final:{' '}
                    {maskCurrency(Number(plan?.amount) - Number(couponData?.discount) * Number(plan?.amount))}
                  </Text>
                </>
              )}

              <Button
                colorScheme="green"
                width="100%"
                type="submit"
                isLoading={isLoading || isLoadingCoupon}
              >
                Continuar para pagamento
              </Button>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
